import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdUpload } from 'react-icons/md';
import { getPlacementPage, editPlacementPage } from 'Services/allApi';
import PlacementSeoPage from './PlacementSeoPage';

function PlacementPage() {
  const [formData, setFormData] = useState({
    heading: '',
    main_sub_heading: '',
    main_head1: '',
    main_head2: '',
  });
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [placementId, setPlacementId] = useState(null);

  useEffect(() => {
    const fetchPlacementData = async () => {
      setLoading(true);
      try {
        const response = await getPlacementPage();
        const result = response?.data || response;

        if (!result || response.error) {
          throw new Error(response.message || 'Failed to fetch data');
        }

        setPlacementId(result.id);
        setFormData({
          heading: result.heading || '',
          main_sub_head: result.main_sub_head || '',
          main_head1: result.main_head1 || '',
          main_head2: result.main_head2 || '',
        });
        setImagePreview(result.bg_img || '');
      } catch (err) {
        setError(err.message);
        toast.error(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchPlacementData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
        alert('Only JPEG, PNG, and GIF files are allowed.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB.');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    if (!formData.heading.trim()) {
      toast.error('Heading is required.');
      return false;
    }
    if (!formData.main_sub_head.trim()) {
      toast.error('Main Sub Heading is required.');
      return false;
    }
    if (!formData.main_head1.trim()) {
      toast.error('Main Head 1 is required.');
      return false;
    }
    if (!formData.main_head2.trim()) {
      toast.error('Main Head 2 is required.');
      return false;
    }
    return true;
  };

  const handleUpdate = async () => {
    if (!placementId) {
      toast.error('Placement page ID is missing!');
      return;
    }

    if (!validateForm()) {
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('heading', formData.heading);
    formDataToSend.append('main_sub_head', formData.main_sub_head);
    formDataToSend.append('main_head1', formData.main_head1);
    formDataToSend.append('main_head2', formData.main_head2);

    if (imageFile) {
      formDataToSend.append('bg_img', imageFile);
    }

    try {
      const response = await editPlacementPage(placementId, formDataToSend);
      if (response.error) {
        throw new Error(response.message || 'Update failed');
      }

      toast.success('Updated successfully!');
    } catch (err) {
      toast.error(`Error: ${err.message}`);
      console.error(err);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="text-red-500">Error: {error}</p>;

  return (
    <>
      <PlacementSeoPage />
      <div className="max-w-8xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-3  dark:!bg-navy-700 dark:text-white">
        <h2 className="text-center text-3xl font-bold mb-5 mt-2">Placement Page</h2>
        <hr className="mb-4" />

        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-4 mt-5">
          <div className="sm:col-span-2">
            <label htmlFor="heading" className="block font-medium text-gray-900">Heading</label>
            <input
              name="heading"
              id="heading"
              value={formData.heading}
              onChange={handleInputChange}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 outline-gray-300 focus:outline-purple-800  dark:!bg-navy-700 dark:text-white"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="main_sub_head" className="block font-medium text-gray-900">Main Sub Heading</label>
            <input
              name="main_sub_head"
              id="main_sub_head"
              value={formData.main_sub_head}
              onChange={handleInputChange}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 outline-gray-300 focus:outline-purple-800  dark:!bg-navy-700 dark:text-white"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="main_head1" className="block font-medium text-gray-900">Main Head 1</label>
            <input
              name="main_head1"
              id="main_head1"
              value={formData.main_head1}
              onChange={handleInputChange}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 outline-gray-300 focus:outline-purple-800  dark:!bg-navy-700 dark:text-white"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="main_head2" className="block font-medium text-gray-900">Main Head 2</label>
            <input
              name="main_head2"
              id="main_head2"
              value={formData.main_head2}
              onChange={handleInputChange}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 outline-gray-300 focus:outline-purple-800  dark:!bg-navy-700 dark:text-white"
            />
          </div>

          <div className="sm:col-span-2 flex flex-col sm:flex-row items-center sm:justify-between mt-4">
            <div className="sm flex justify-center mb-4 sm:mb-0">
              {imagePreview && (
                <div className="max-w-full max-h-64">
                  <img
                    src={imagePreview}
                    alt="Image Preview"
                    className="w-full rounded shadow-md"
                    style={{ height: '130px' }}
                  />
                </div>
              )}
              <div className="sm text-center ms-2 mt-4">
                <label htmlFor="bg_image" className="block font-medium text-gray-900 mb-2">
                  Background Image
                </label>
                <input
                  type="file"
                  id="bg_image"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <button
                  type="button"
                  onClick={() => document.getElementById('bg_image').click()}
                  className="px-4 py-2 ms-2 bg-purple-600 text-white rounded-md shadow hover:bg-purple-700"
                >
                  <MdUpload />
                </button>
                <p className="mt-2" style={{ fontSize: '10px' }}>The height and width must be 1996 × 557</p>

              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleUpdate}
            className="px-4 py-2 bg-purple-600 text-white rounded-md shadow hover:bg-purple-700"
          >
            Update
          </button>
        </div>
        <ToastContainer position="top-center" autoClose={5000} />
      </div>
    </>
  );
}

export default PlacementPage;
